/**
 * A group's «Vídeos» table, as every list of the back-office has it: a search, a funnel, an order,
 * a selection, and what can be done to the selection.
 *
 * The members arrive whole — upstream does not page them — so the search, the filters and the order
 * narrow and sort in the browser (`sortRows`), and none of them asks again. What can be done to
 * several at once is taking them out of the group: in one call (`removeAll`), and the tally says how
 * each went.
 */
import { ApiError } from '@shared/infrastructure/http/api-error.js';
import type { GroupMember } from '../model.js';
import { formatCount } from './format-count.js';
import { memberLabel } from './group-resolution.js';
import { type SortOption, sortOptionOf, sortRows } from './list-sort.js';

// ── Filters ──────────────────────────────────────────────────────────────────

/** As the gallery's label filter spells them (`VideoLabelFilter`). */
export type MemberLabelFilter = 'incoherent' | 'coherent' | 'unlabelled';

/** The member's latest run in the execution shown (`resolved.bucket`), as the table words it. */
export type MemberStateFilter = 'ok' | 'not_analysed' | 'pending' | 'never_run';

export const MEMBER_LABEL_FILTERS: ReadonlyArray<{
  key: MemberLabelFilter;
  label: string;
}> = Object.freeze([
  { key: 'incoherent', label: 'Mentira' },
  { key: 'coherent', label: 'Verdad' },
  { key: 'unlabelled', label: 'Sin etiquetar' },
]);

export const MEMBER_STATE_FILTERS: ReadonlyArray<{
  key: MemberStateFilter;
  label: string;
}> = Object.freeze([
  { key: 'ok', label: 'Analizado' },
  { key: 'not_analysed', label: 'Falló o cancelado' },
  { key: 'pending', label: 'En marcha' },
  { key: 'never_run', label: 'No está en la ejecución' },
]);

/** What the search box and the funnel hold. None ticked is every label, every state. */
export interface MemberFilterState {
  search: string;
  labels: MemberLabelFilter[];
  states: MemberStateFilter[];
}

export function emptyMemberFilters(): MemberFilterState {
  return { search: '', labels: [], states: [] };
}

/** The funnel's badge: the kinds of filter narrowing the table. The search is in sight already. */
export function activeMemberFilterCount(state: MemberFilterState): number {
  return (state.labels.length > 0 ? 1 : 0) + (state.states.length > 0 ? 1 : 0);
}

function labelOf(member: GroupMember): MemberLabelFilter {
  return member.label ?? 'unlabelled';
}

/** A bucket upstream adds tomorrow reads as «not in it», as the table's chip reads it. */
function stateOf(member: GroupMember): MemberStateFilter {
  const bucket = member.resolved.bucket;

  return bucket === 'ok' || bucket === 'not_analysed' || bucket === 'pending'
    ? bucket
    : 'never_run';
}

/** «Álvaro» found by «alvaro», and a video by its id. */
const fold = (text: string): string =>
  text
    .normalize('NFD')
    .replaceAll(/\p{Diacritic}/gu, '')
    .toLowerCase();

export function filterMembers(
  members: readonly GroupMember[],
  state: MemberFilterState,
): GroupMember[] {
  const term = fold(state.search.trim());

  return members.filter(
    (member) =>
      (term === '' ||
        fold(memberLabel(member)).includes(term) ||
        String(member.sourceId) === term) &&
      (state.labels.length === 0 || state.labels.includes(labelOf(member))) &&
      (state.states.length === 0 || state.states.includes(stateOf(member))),
  );
}

// ── Order ────────────────────────────────────────────────────────────────────

export type MemberSortField = 'name' | 'added_at' | 'state' | 'label';

export const MEMBER_SORTS: readonly SortOption<MemberSortField>[] = Object.freeze([
  {
    key: 'name:asc',
    label: 'Nombre (A-Z)',
    orderBy: 'name',
    orderDir: 'asc',
  },
  {
    key: 'name:desc',
    label: 'Nombre (Z-A)',
    orderBy: 'name',
    orderDir: 'desc',
  },
  {
    key: 'added_at:desc',
    label: 'Añadidos más recientemente',
    orderBy: 'added_at',
    orderDir: 'desc',
  },
  {
    key: 'added_at:asc',
    label: 'Añadidos hace más tiempo',
    orderBy: 'added_at',
    orderDir: 'asc',
  },
  {
    key: 'state:asc',
    label: 'Por estado, los fallidos primero',
    orderBy: 'state',
    orderDir: 'asc',
  },
  {
    key: 'label:asc',
    label: 'Por etiqueta',
    orderBy: 'label',
    orderDir: 'asc',
  },
] satisfies SortOption<MemberSortField>[]);

/** What needs a person first: what failed, then what is still running, then the rest. */
const STATE_RANK: Readonly<Record<MemberStateFilter, number>> = Object.freeze({
  not_analysed: 0,
  pending: 1,
  never_run: 2,
  ok: 3,
});

/** Incoherences first: they are what a coherence engine is measured on. */
const LABEL_RANK: Readonly<Record<MemberLabelFilter, number>> = Object.freeze({
  incoherent: 0,
  coherent: 1,
  unlabelled: 2,
});

export function sortMembers(members: readonly GroupMember[], key: string): GroupMember[] {
  return sortRows(members, sortOptionOf(MEMBER_SORTS, key), (member, field) => {
    switch (field) {
      case 'name':
        return memberLabel(member);
      case 'added_at':
        return member.addedAt;
      case 'state':
        return STATE_RANK[stateOf(member)];
      default:
        return LABEL_RANK[labelOf(member)];
    }
  });
}

// ── Taking several out ───────────────────────────────────────────────────────

export function removeMembersConfirmation(members: readonly GroupMember[]): {
  title: string;
  description: string;
} {
  const title =
    members.length === 1 && members[0]
      ? `¿Quitar «${memberLabel(members[0])}» del grupo?`
      : `¿Quitar ${formatCount(members.length)} vídeos del grupo?`;

  return {
    title,
    description:
      'Salen del grupo y de sus próximas ejecuciones. No se borra nada: ni los vídeos, ni sus etiquetas, ni sus análisis, y las ejecuciones anteriores no cambian.',
  };
}

export interface RemovalTally {
  removed: number[];
  /** Out of the group already — somebody else took it out: the outcome asked for, not a failure. */
  gone: number[];
  failed: number[];
  firstFailure: unknown;
}

/**
 * Takes the members out in one call (2026-10-29: one dataset version for all of them) and says how
 * each went. Never throws: a failure of the call fails every member with it.
 */
export async function removeAll(
  memberIds: readonly number[],
  remove: (memberIds: readonly number[]) => Promise<{ removed: number[]; missing: number[] }>,
): Promise<RemovalTally> {
  try {
    const answer = await remove(memberIds);
    return { removed: answer.removed, gone: answer.missing, failed: [], firstFailure: undefined };
  } catch (error) {
    return { removed: [], gone: [], failed: [...memberIds], firstFailure: error };
  }
}

/**
 * Takes each member out in turn and says how each went. Never throws: a failure is one member's
 * outcome, and the ones after it are still taken out.
 */
export async function removeEach(
  memberIds: readonly number[],
  remove: (memberId: number) => Promise<unknown>,
): Promise<RemovalTally> {
  const tally: RemovalTally = {
    removed: [],
    gone: [],
    failed: [],
    firstFailure: undefined,
  };

  for (const memberId of memberIds) {
    try {
      await remove(memberId);
      tally.removed.push(memberId);
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        tally.gone.push(memberId);
      } else {
        tally.failed.push(memberId);
        tally.firstFailure ??= error;
      }
    }
  }

  return tally;
}

/** What the toast says: how many went, and how many could not. */
export function removalSummary(tally: RemovalTally): {
  tone: 'success' | 'error';
  text: string;
} {
  const out = tally.removed.length + tally.gone.length;
  const done = `${formatCount(out)} ${out === 1 ? 'vídeo quitado' : 'vídeos quitados'} del grupo`;

  if (tally.failed.length === 0) return { tone: 'success', text: done };

  const failed = `${formatCount(tally.failed.length)} no se ${tally.failed.length === 1 ? 'ha' : 'han'} podido quitar`;

  return { tone: 'error', text: out > 0 ? `${done}; ${failed}` : failed };
}

// ── Pages ────────────────────────────────────────────────────────────────────

/** What the size selector offers. Ten by default: a page of thumbnails fits on one screen. */
export const MEMBER_PAGE_SIZES: readonly number[] = Object.freeze([10, 25, 50, 100]);

export const DEFAULT_MEMBER_PAGE_SIZE = 10;

/** How many pages the rows fill; one even when there are none, so «página 1 de 1» reads right. */
export function memberPageCount(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / pageSize));
}

/** The rows of one page, the page brought inside the ones there are. */
export function memberPage<T>(rows: readonly T[], page: number, pageSize: number): T[] {
  const last = memberPageCount(rows.length, pageSize);
  const within = Math.max(1, Math.min(page, last));

  return rows.slice((within - 1) * pageSize, within * pageSize);
}
